/**
 * Tenant Service — tenant info and onboarding status.
 *
 * Calls GET/PUT /api/tenant on the chat-api Lambda.
 * Auth: Cognito JWT via authService.getIdToken().
 */

import authService from './authService';
import { API_CONFIG } from '../config/api';

// ─── Types ─────────────────────────────────────────────────────────

export interface OnboardingStatus {
  connected: boolean;
  employeesSelected: boolean;
}

export interface TenantInfo {
  tenantId: string;
  domain: string;
  displayName: string;
  dataSourceType: string;
  maxEmployees: number;
  onboardingStatus: OnboardingStatus;
}

export interface TenantUpdateFields {
  displayName?: string;
  domain?: string;
  dataSourceType?: string;
  subscriptionTier?: string;
  maxEmployees?: number;
  employeeNotificationsEnabled?: boolean;
}

// ─── HTTP helper ───────────────────────────────────────────────────

async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await authService.getIdToken();

  const response = await fetch(`${API_CONFIG.BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const message = errorBody.error || `Request failed with status ${response.status}`;
    const error = new Error(message) as any;
    error.errorCode = errorBody.error_code;
    error.status = response.status;
    throw error;
  }

  return response.json();
}

// ─── Service methods ───────────────────────────────────────────────

/**
 * Get current tenant info with computed onboarding status.
 */
export async function getTenant(): Promise<TenantInfo> {
  return apiRequest<TenantInfo>('/api/tenant');
}

/**
 * Update tenant settings. Admin/owner only.
 * Only allowlisted fields are accepted by the backend.
 */
export async function updateTenant(updates: TenantUpdateFields): Promise<TenantInfo> {
  return apiRequest<TenantInfo>('/api/tenant', {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

const tenantService = {
  getTenant,
  updateTenant,
};

export default tenantService;
